import axios from 'helpers/api';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import React, { useEffect, useState, useMemo } from 'react';
import { Col, Dropdown, Form, Modal, Row, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import { storeList, getStoredStoreId, setStoredStoreId } from 'components/common/StoreList';

const AddHoldOrderDetailModal = ({
    show,
    setShow,
    initialData,
    patientId,
    patientName,
    facilityId,
    facilityName,
    onSuccess
}) => {
    const [drugOptions, setDrugOptions] = useState([]);
    const [deliveryDates, setDeliveryDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedDrugs, setSelectedDrugs] = useState([]);
    const [newDrug, setNewDrug] = useState("");
    const [note, setNote] = useState("");
    const [editableQuantities, setEditableQuantities] = useState({});
    const [orderTypeValue, setOrderTypeValue] = useState('4');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [selectedFacility, setSelectedFacility] = useState({});
    const [facilitiesData] = useState(JSON.parse(localStorage.getItem("facilitiesData")) || []);
    const [patientOptions, setPatientOptions] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState({ label: 'Select Patient', value: '' });
    const [selectedPatientFlag, setSelectedPatientFlag] = useState(false);
    const [rxNumber, setRxNumber] = useState("");
    const [deliveryTypeOptions, setDeliveryTypeOptions] = useState([]);

    const [theme, setTheme] = useState(document.documentElement.getAttribute('data-bs-theme') || 'light');
    const [storeOptions] = useState([{ key: "Select Store", value: "" }, ...storeList]);
    const savedStoreId = getStoredStoreId();
    const initialStoreOption = storeOptions.find(opt => String(opt.value) === String(savedStoreId)) || { key: "Select Store", value: "" };
    const [selectedStoreValue, setSelectedStoreValue] = useState(initialStoreOption);

    const masters = JSON.parse(localStorage.getItem("masters")) || {};
    const pharmacyAttentionReasons = masters?.pharmacy_attentions || [];
    const callbackReasons = masters?.callback_masters || [];
    const [isPharmacyAttention, setIsPharmacyAttention] = useState(0);
    const [isCallback, setIsCallback] = useState(0);
    const [pharmacyAttentionId, setPharmacyAttentionId] = useState("");
    const [callbackMasterId, setCallbackMasterId] = useState("");

    const orderStatusList = useMemo(() => {
        let orderStatusInternal = [];
        try {
            orderStatusInternal = JSON.parse(localStorage.getItem("order_status_internal")) || [];
        } catch (e) {}

        if (orderStatusInternal.length === 0 && Array.isArray(masters?.order_status)) {
            orderStatusInternal = masters.order_status.map(item => ({
                key: item.order_status,
                value: item.id
            }));
        }

        return orderStatusInternal.filter(
            item => !['Mobile Order', 'Done', 'Discard'].includes(item.key)
        );
    }, [show, masters]);

    const loadDeliveryTypeOptions = () => {
        let delivery_types = {};
        try {
            delivery_types = JSON.parse(localStorage.getItem("delivery_types")) || {};
        } catch (e) {}

        if (Object.keys(delivery_types).length === 0 && masters?.delivery_locations) {
            masters.delivery_locations.forEach(item => {
                delivery_types[item.id] = item.delivery_location;
            });
        }

        return [
            { value: "", label: "Select a type" },
            ...Object.keys(delivery_types).map((key) => ({
                value: key,
                label: delivery_types[key]
            }))
        ];
    };

    const orderType = useMemo(() => {
        let orderTypeVal = {};
        try {
            orderTypeVal = JSON.parse(localStorage.getItem("order_types")) || {};
        } catch (e) {}

        if (Object.keys(orderTypeVal).length === 0 && Array.isArray(masters?.order_types)) {
            masters.order_types.forEach(item => {
                orderTypeVal[item.id] = item.order_type;
            });
        }

        return [
            { value: "", label: "Select Order Type" },
            ...Object.keys(orderTypeVal)
                .filter(key => orderTypeVal[key] !== "Label Change")
                .filter(key => orderTypeVal[key] !== "DC Medication")
                .map((key) => ({
                    value: parseInt(key, 10),
                    label: orderTypeVal[key]
                }))
        ];
    }, [show, masters]);

    const initialFormData = {
        patient_id: '',
        delivery_date: '',
        order_type: '4',
        note: '',
        orderstatus: '2',
        deliveryOn: '1',
        prescriptions: []
    };
    const [formData, setFormData] = useState(initialFormData);

    useEffect(() => {
        if (show) {
            setDeliveryTypeOptions(loadDeliveryTypeOptions());

            const dates = getNextWorkingDays();
            setDeliveryDates(dates);
            if (dates.length > 0) {
                setSelectedDate(dates[0].value);
            }

            const targetPatientId = initialData?.patient_id || patientId;
            const targetPatientName = initialData?.patient_name || (initialData?.patient_lastname && initialData?.patient_firstname ? `${initialData.patient_lastname}, ${initialData.patient_firstname}` : initialData?.patient) || patientName;
            const targetFacilityId = initialData?.facility_id || facilityId;
            const targetFacilityName = initialData?.facility_name || facilityName;

            if (targetPatientId || targetPatientName) {
                const patientObj = {
                    value: targetPatientId || '',
                    label: targetPatientName || '',
                    facility_id: targetFacilityId || ''
                };
                setSelectedPatient(patientObj);
                setSelectedPatientFlag(true);
                setPatientOptions([patientObj]);

                const matchedFacility = facilitiesData?.find(
                    facility => Number(facility.value) === Number(targetFacilityId)
                ) || { key: targetFacilityName || '', value: targetFacilityId };

                setSelectedFacility(matchedFacility);
                if (targetPatientId) {
                    fetchData(targetPatientId);
                }
            }

            setOrderTypeValue('4');
            setFormData(prev => ({
                ...prev,
                order_type: '4',
                orderstatus: '2',
                deliveryOn: '1',
                patient_id: targetPatientId ? String(targetPatientId) : ''
            }));

            if (initialData) {
                if (initialData.drug) {
                    setNewDrug(initialData.drug);
                } else {
                    setNewDrug("");
                }

                if (initialData.rx_number) {
                    setRxNumber(String(initialData.rx_number));
                } else {
                    setRxNumber("");
                }
            } else {
                setNewDrug("");
                setRxNumber("");
            }
        }
    }, [show, initialData, patientId, patientName, facilityId, facilityName]);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const currentTheme = document.documentElement.getAttribute('data-bs-theme') || 'light';
            setTheme(currentTheme);
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-bs-theme'],
        });

        return () => observer.disconnect();
    }, []);

    const getNextWorkingDays = () => {
        const days = [];
        let currentDate = new Date();
        for (let i = 0; i < 5; i++) {
            const label = `${currentDate.toLocaleDateString('en-US', { weekday: 'long' })} (${formatDate(currentDate)})`;
            days.push({
                label,
                value: `${String(currentDate.getMonth() + 1).padStart(2, '0')}/${String(currentDate.getDate()).padStart(2, '0')}/${currentDate.getFullYear()}`
            });
            currentDate.setDate(currentDate.getDate() + 1);
        }
        return days;
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
        });
    };

    const handleQtyChange = (drugName, maxQty, value) => {
        const numericValue = parseFloat(value);
        if (isNaN(numericValue) || numericValue <= 0) {
            setEditableQuantities((prev) => ({
                ...prev,
                [drugName]: ""
            }));
        } else {
            setEditableQuantities((prev) => ({
                ...prev,
                [drugName]: value
            }));
        }
    };

    async function fetchData(pid) {
        if (!pid) return;
        try {
            let tokenStr = localStorage.getItem("accessToken");
            setFormData((prev) => ({
                ...prev,
                patient_id: String(pid)
            }));
            const { data } = await axios.get(
                `${process.env.REACT_APP_SERVER_URL}/orders/list-refill-prescriptions/${pid}`,
                { headers: { Authorization: `Bearer ${tokenStr}` } }
            );
            let objData = data.prescriptions || [];
            let results = [];
            objData
                .sort((a, b) => a.drug.localeCompare(b.drug))
                .forEach((value) => {
                    results.push({
                        name: value.drug + " " + value.strength_value + " " + value.strength,
                        rxNumber: value.pharmacy_order_id != null ? value.pharmacy_order_id : 0,
                        qty: value.qty ? parseFloat(value.qty) : 0
                    });
                });
            setDrugOptions(results);
        } catch (err) {
            console.error("Error fetching refill prescriptions:", err);
        }
    }

    const handleDrugSelection = (drug) => {
        if (!selectedDrugs.some((selected) => selected.name === drug.name)) {
            setSelectedDrugs([...selectedDrugs, drug]);
            setDrugOptions(drugOptions.filter((value) => value.rxNumber !== drug.rxNumber));

            setEditableQuantities((prev) => ({
                ...prev,
                [drug.rxNumber]: drug.qty.toString()
            }));
        }
    };

    const handleRemoveDrug = (drug) => {
        setSelectedDrugs(selectedDrugs.filter((selected) => selected.name !== drug.name));
        drugOptions.push(drug);
    };

    const handleFieldChange = (e) => {
        const value = e.target.value;
        setOrderTypeValue(value);

        setFormData((prev) => ({
            ...prev,
            order_type: value
        }));
        setErrors((prev) => ({
            ...prev,
            order_type: ""
        }));
    };

    const handleOrderStatusChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            orderstatus: e.target.value
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        let newErrors = {};
        const type = parseInt(orderTypeValue);
        const isTask = parseInt(formData.orderstatus) === 99;

        if (!selectedPatient.value) {
            newErrors.patient = "Patient is required.";
        }

        if (!isTask && !orderTypeValue) {
            newErrors.order_type = "Order Type is required.";
        }

        if (selectedDate === "") {
            newErrors.delivery_date = "Delivery Date is required.";
        }

        if (formData.orderstatus === "") {
            newErrors.orderstatus = "Order Status is required.";
        }

        if (!isTask && formData.deliveryOn === "") {
            newErrors.deliveryOn = "Delivery On is required.";
        }

        const typesWithDrugs = [1, 2, 4, 7, 8];
        if (!isTask && typesWithDrugs.includes(type)) {
            if (type === 4 && !newDrug.trim()) {
                newErrors.newDrug = "Please enter the new drug name.";
            } else if (type !== 4 && selectedDrugs.length === 0) {
                newErrors.selectedDrugs = "Please select at least one drug.";
            }
        }

        if (!isTask && type === 4 && !newDrug.trim()) {
            newErrors.newDrug = "Please enter the new drug name.";
        }

        if (!isTask && type === 3 && note.trim() === "") {
            newErrors.note = "Please enter a note.";
        }

        if (isPharmacyAttention === 1 && !pharmacyAttentionId) {
            newErrors.pharmacy_attention_id = "Pharmacy attention reason is required.";
        }

        if (isCallback === 1 && !callbackMasterId) {
            newErrors.callback_master_id = "Callback reason is required.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    async function handleSubmit(e) {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);

        const isTask = parseInt(formData.orderstatus) === 99;
        const type = Number(orderTypeValue);

        const deliveryTypeMatch = deliveryTypeOptions.find(
            (typeOption) => Number(typeOption.value) === Number(formData.deliveryOn || "1")
        );

        let payload = {
            patient_id: selectedPatient.value,
            delivery_date: selectedDate,
            order_type: Number(type),
            note: note,
            order_status: formData.orderstatus,
            delivery_type: deliveryTypeMatch ? deliveryTypeMatch.value : "1",
            pickup_store_id: (Number(formData.deliveryOn) === 5 && selectedStoreValue) ? selectedStoreValue.value : 0,
            is_pharmacy_attention: isPharmacyAttention,
            is_callback: isCallback,
            pharmacy_attention_id: isPharmacyAttention === 1 ? pharmacyAttentionId : 0,
            callback_master_id: isCallback === 1 ? callbackMasterId : 0,
        };

        if (!isTask && type === 4) {
            payload.new_drug = newDrug.trim();
            payload.rx_number = rxNumber;
        }

        if (type !== 4 && selectedDrugs.length > 0) {
            payload.prescriptions = JSON.stringify(
                selectedDrugs.map((drug) => ({
                    rx_number: drug.rxNumber,
                    quantity: editableQuantities[drug.rxNumber] || drug.qty,
                }))
            );
        }

        const submissionData = new FormData();
        Object.keys(payload).forEach((key) => submissionData.append(key, payload[key]));

        try {
            const tokenStr = localStorage.getItem("accessToken");
            const response = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/orders/create-prescription-order/`,
                submissionData,
                { headers: { Authorization: `Bearer ${tokenStr}` } }
            );

            if (response.data && response.data.status) {
                toast.success("Prescription Order added successfully");
                resetForm();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                toast.info(response.data?.message || "Error occurred while adding/updating Prescription Order.");
            }
        } catch (error) {
            console.error("Order submission error:", error);
            toast.error("An error occurred while creating order. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    const handlePatientSelection = (selectedOption) => {
        setSelectedPatient(selectedOption || { label: 'Select Patient', value: '' });
        if (selectedOption && selectedOption.value) {
            setSelectedPatientFlag(true);
            const fac = facilitiesData?.find(
                (facility) => Number(facility.value) === Number(selectedOption.facility_id)
            );
            setSelectedFacility(fac || {});
            fetchData(selectedOption.value);
        } else {
            setSelectedPatientFlag(false);
            setSelectedFacility({});
        }
    };

    const resetForm = () => {
        setDrugOptions([]);
        setSelectedDrugs([]);
        setNewDrug("");
        setNote("");
        setEditableQuantities({});
        setRxNumber("");
        setFormData(initialFormData);
        setOrderTypeValue('4');
        setErrors({});
        setSelectedFacility({});
        setPatientOptions([]);
        setSelectedPatient({ label: 'Select Patient', value: '' });
        setSelectedPatientFlag(false);
        setDeliveryDates([]);
        setSelectedDate('');
        setShow(false);
        setLoading(false);
        setIsPharmacyAttention(0);
        setIsCallback(0);
        setPharmacyAttentionId("");
        setCallbackMasterId("");
    };

    const loadOptions = async (inputValue) => {
        if (!inputValue) return [];

        try {
            const tokenStr = localStorage.getItem("accessToken");
            const response = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/patients/search-patient`,
                { search: inputValue },
                { headers: { Authorization: `Bearer ${tokenStr}` } }
            );

            const data = response.data.data.map((item) => ({
                value: item.id,
                label: `${item.last_name}, ${item.first_name}`,
                facility_id: item.facility_id,
            }));

            setPatientOptions(data);
            return data;
        } catch (error) {
            console.error("Error loading patients:", error);
            return [];
        }
    };

    return (
        <Modal onHide={resetForm} show={show} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title as="h5">ADD Order</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-body-tertiary p-4">
                <Form onSubmit={handleSubmit}>
                    <Row className='mb-3'>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Order Status</Form.Label>
                                <Form.Select
                                    name="orderstatus"
                                    value={formData.orderstatus}
                                    onChange={(e) => {
                                        handleOrderStatusChange(e);
                                        if (e.target.value === "99") {
                                            setFormData(prev => ({ ...prev, orderstatus: "99", order_type: "9" }));
                                            setOrderTypeValue(10);
                                        } else {
                                            setOrderTypeValue('4');
                                        }
                                        setErrors(prev => ({ ...prev, orderstatus: "", deliveryOn: "" }));
                                    }}
                                >
                                    {orderStatusList.map((order) => (
                                        <option key={order.value} value={order.value}>{order.key}</option>
                                    ))}
                                </Form.Select>
                                {errors.orderstatus && <small className="text-danger">{errors.orderstatus}</small>}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className='required'>Delivery On</Form.Label>
                                <Form.Select
                                    name="deliveryOn"
                                    value={formData.deliveryOn}
                                    onChange={handleInputChange}
                                    disabled={parseInt(formData.orderstatus) === 99}
                                >
                                    {deliveryTypeOptions?.map((type) => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </Form.Select>
                                {errors.deliveryOn && <small className="text-danger">{errors.deliveryOn}</small>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Store</Form.Label>
                                <ThemedSelect
                                    name="store"
                                    closeMenuOnSelect={true}
                                    placeholder='Select store'
                                    classNamePrefix="react-select"
                                    options={storeOptions}
                                    value={selectedStoreValue}
                                    getOptionLabel={option => option.key}
                                    getOptionValue={option => option.value}
                                    onChange={(e) => {
                                        setSelectedStoreValue(e);
                                        if (e && e.value !== undefined) {
                                            setStoredStoreId(e.value);
                                        }
                                    }}
                                    isDisabled={Number(formData.deliveryOn) !== 5}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Label className="required">Patient</Form.Label>
                            {selectedPatientFlag ? (
                                <Form.Control
                                    type="text"
                                    value={selectedPatient?.label || selectedPatient?.name || ''}
                                    readOnly
                                />
                            ) : (
                                <AsyncSelect
                                    cacheOptions
                                    loadOptions={loadOptions}
                                    value={selectedPatient}
                                    defaultOptions={patientOptions}
                                    getOptionLabel={(option) => option.label || option.name}
                                    getOptionValue={(option) => option.value || option.id}
                                    onChange={handlePatientSelection}
                                    placeholderText="Type to search..."
                                    styles={{
                                        control: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: theme === 'dark' ? '#2d2f38' : '#fff',
                                            color: theme === 'dark' ? '#f8f9fa' : '#212529',
                                            borderColor: state.isFocused ? '#80bdff' : '#ced4da',
                                            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
                                            '&:hover': {
                                                borderColor: '#80bdff'
                                            }
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            backgroundColor: theme === 'dark' ? '#2d2f38' : '#fff',
                                            zIndex: 5
                                        }),
                                        option: (provided, { isFocused, isSelected }) => ({
                                            ...provided,
                                            backgroundColor: isSelected
                                                ? theme === 'dark' ? '#3a3b3f' : '#e9ecef'
                                                : isFocused
                                                    ? theme === 'dark' ? '#44464e' : '#f1f1f1'
                                                    : theme === 'dark' ? '#2d2f38' : '#fff',
                                            color: theme === 'dark' ? '#f8f9fa' : '#212529',
                                            cursor: 'pointer',
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: theme === 'dark' ? '#f8f9fa' : '#212529'
                                        }),
                                        placeholder: (provided) => ({
                                            ...provided,
                                            color: theme === 'dark' ? '#adb5bd' : '#6c757d'
                                        }),
                                        input: (provided) => ({
                                            ...provided,
                                            color: theme === 'dark' ? '#f8f9fa' : '#212529'
                                        }),
                                        dropdownIndicator: (provided) => ({
                                            ...provided,
                                            color: theme === 'dark' ? '#adb5bd' : '#495057',
                                            '&:hover': {
                                                color: '#80bdff'
                                            }
                                        }),
                                        indicatorSeparator: (provided) => ({
                                            ...provided,
                                            backgroundColor: theme === 'dark' ? '#44464e' : '#ced4da'
                                        }),
                                    }}
                                />
                            )}
                            {errors.patient && <small className="text-danger">{errors.patient}</small>}
                        </Col>
                    </Row>

                    {selectedPatientFlag && (
                        <>
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Label>Facility</Form.Label>
                                    <Form.Control type="text" value={selectedFacility?.key || selectedFacility?.name || ''} readOnly />
                                </Col>
                            </Row>
                            <Row className='mb-3'>
                                <Col md={6}>
                                    <Form.Label className="required">Delivery Date</Form.Label>
                                    <Form.Select name="selectdeliverydate" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>
                                        {deliveryDates.map((date, index) => (
                                            <option key={index} value={date.value}>{date.label}</option>
                                        ))}
                                    </Form.Select>
                                    {errors.delivery_date && <small className="text-danger">{errors.delivery_date}</small>}
                                </Col>
                                <Col md={6}>
                                    <Form.Label className="required">Order Type</Form.Label>
                                    <Form.Select name="ordertype" value={orderTypeValue} disabled onChange={handleFieldChange}>
                                        {orderType.map((type) => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </Form.Select>
                                    {errors.order_type && <small className="text-danger">{errors.order_type}</small>}
                                </Col>
                            </Row>
                        </>
                    )}

                    {[1, 2, 7, 8, 10].includes(parseInt(orderTypeValue)) && (
                        <>
                            <Row className="mt-3">
                                <Col md={12}>
                                    <Dropdown>
                                        <Dropdown.Toggle variant="outline-secondary" className='w-50'>Select drug to order</Dropdown.Toggle>
                                        <Dropdown.Menu className='w-50' style={{ maxHeight: "200px", overflowY: "auto" }}>
                                            {drugOptions.map((drug, index) => (
                                                <Dropdown.Item key={index} onClick={() => handleDrugSelection(drug)}>{drug.name}</Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    {errors.selectedDrugs && <small className="text-danger">{errors.selectedDrugs}</small>}
                                </Col>
                            </Row>
                            {selectedDrugs.length > 0 && (
                                <>
                                    <Row className="mt-3 mb-1 g-2">
                                        <Col xs={6} md={7}>
                                            <Form.Label className="mb-1 fw-semibold">Drug Name</Form.Label>
                                        </Col>

                                        <Col xs={3} md={2}>
                                            <Form.Label className="mb-1 fw-semibold">Rx Number</Form.Label>
                                        </Col>

                                        <Col xs={2} md={2}>
                                            <Form.Label className="mb-1 fw-semibold">Qty</Form.Label>
                                        </Col>

                                        <Col xs={1}>
                                        </Col>
                                    </Row>

                                    {selectedDrugs.map((drug, index) => (
                                        <Row key={index} className="mb-2 align-items-center g-2">
                                            <Col xs={6} md={7}>
                                                <Form.Control
                                                    type="text"
                                                    value={drug.name}
                                                    readOnly
                                                    size="sm"
                                                />
                                            </Col>

                                            <Col xs={3} md={2}>
                                                <Form.Control
                                                    type="text"
                                                    value={drug.rxNumber}
                                                    readOnly
                                                    size="sm"
                                                />
                                            </Col>

                                            <Col xs={2} md={2}>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    size="sm"
                                                    value={editableQuantities[drug.rxNumber] ?? drug.qty}
                                                    placeholder="Qty"
                                                    onChange={(e) =>
                                                        handleQtyChange(drug.rxNumber, drug.qty, e.target.value)
                                                    }
                                                />
                                            </Col>

                                            <Col xs={1} className="text-center">
                                                <span
                                                    className="badge bg-danger text-white cursor-pointer"
                                                    style={{ fontSize: "14px", padding: "6px 8px" }}
                                                    onClick={() => handleRemoveDrug(drug)}
                                                >
                                                    ×
                                                </span>
                                            </Col>
                                        </Row>
                                    ))}
                                </>
                            )}
                        </>
                    )}

                    {parseInt(orderTypeValue) === 4 && (
                        <>
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Label className="required">New Drug Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter new drug name"
                                        value={newDrug}
                                        onChange={(e) => setNewDrug(e.target.value)}
                                    />
                                    {errors.newDrug && <small className="text-danger">{errors.newDrug}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Label>RX Number</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter RX number"
                                        value={rxNumber}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                setRxNumber(value);
                                            }
                                        }}
                                    />
                                    {errors.rxNumber && <small className="text-danger">{errors.rxNumber}</small>}
                                </Col>
                            </Row>
                        </>
                    )}

                    {orderTypeValue && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Label>
                                    Notes {parseInt(orderTypeValue) === 3 && <span className="text-danger"> (Required)</span>}
                                </Form.Label>
                                <Form.Control as="textarea" rows={1} value={note} onChange={(e) => setNote(e.target.value)} />
                                {errors.note && <small className="text-danger">{errors.note}</small>}
                            </Col>
                        </Row>
                    )}

                    {Number(formData.orderstatus) === 99 && selectedPatientFlag && (
                        <>
                            <Row className="align-items-center mb-3">
                                <Col md={3} className="d-flex align-items-center">
                                    <Form.Check
                                        type="switch"
                                        id="is_pharmacy_attention"
                                        label="Pharmacy Attention"
                                        checked={isPharmacyAttention === 1 && isCallback === 0}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setIsPharmacyAttention(checked ? 1 : 0);
                                            setPharmacyAttentionId("");
                                            if (checked) {
                                                setIsCallback(0);
                                                setCallbackMasterId("");
                                            }
                                            setErrors(prev => ({ ...prev, pharmacy_attention_id: "", callback_master_id: "" }));
                                        }}
                                    />
                                </Col>
                                {isPharmacyAttention === 1 && (
                                    <Col md={9}>
                                        <Form.Select
                                            value={pharmacyAttentionId}
                                            onChange={(e) => {
                                                setPharmacyAttentionId(e.target.value);
                                                setErrors(prev => ({ ...prev, pharmacy_attention_id: "" }));
                                            }}
                                        >
                                            <option value="">Select reason</option>
                                            {pharmacyAttentionReasons.map((reason) => (
                                                <option key={reason.id} value={reason.id}>
                                                    {reason.pharmacy_attention_reason || reason.reason || reason.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {errors.pharmacy_attention_id && <small className="text-danger">{errors.pharmacy_attention_id}</small>}
                                    </Col>
                                )}
                            </Row>
                            <Row className="align-items-center mb-3">
                                <Col md={3} className="d-flex align-items-center">
                                    <Form.Check
                                        type="switch"
                                        id="is_callback"
                                        label="Callback"
                                        checked={isCallback === 1 && isPharmacyAttention === 0}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setIsCallback(checked ? 1 : 0);
                                            setCallbackMasterId("");
                                            if (checked) {
                                                setIsPharmacyAttention(0);
                                                setPharmacyAttentionId("");
                                            }
                                            setErrors(prev => ({ ...prev, callback_master_id: "", pharmacy_attention_id: "" }));
                                        }}
                                    />
                                </Col>
                                {isCallback === 1 && (
                                    <Col md={9}>
                                        <Form.Select
                                            value={callbackMasterId}
                                            onChange={(e) => {
                                                setCallbackMasterId(e.target.value);
                                                setErrors(prev => ({ ...prev, callback_master_id: "" }));
                                            }}
                                        >
                                            <option value="">Select reason</option>
                                            {callbackReasons.map((reason) => (
                                                <option key={reason.id} value={reason.id}>
                                                    {reason.callback_reason || reason.reason || reason.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {errors.callback_master_id && <small className="text-danger">{errors.callback_master_id}</small>}
                                    </Col>
                                )}
                            </Row>
                        </>
                    )}

                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Label className="text-danger text-center fw-bold d-block">
                                *Please call pharmacy for urgent or after hours deliveries.
                            </Form.Label>
                        </Col>
                    </Row>

                    <Row className='d-flex justify-content-center'>
                        <Button type="submit"
                            variant="primary"
                            name="addordermodalsubmit"
                            className='w-25'
                            disabled={loading}>
                            {loading ? <Spinner size="sm" animation="border" /> : "Submit"}
                        </Button>
                    </Row>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default AddHoldOrderDetailModal;
